'use client'

import { useQuery } from '@tanstack/react-query'
import { ApiError, ehRedeBloqueada } from '@/lib/api/errors'
import { checkPermission } from '@/lib/permissions/check'
import { USERS_PERMISSIONS } from '@/lib/permissions/users'
import { useAuth } from '@/providers/AuthContext'
import { fetchSystemUsers } from '@/services/users'
import type ISystemUser from '@/types/SystemUser'
import type { AuthUser } from '@/types/auth'

/** Permission que libera o `GET /users/directory`. **`users:read` não serve** —
 *  aquele é o grant de gestão, de outra rota. */
export const SYSTEM_USERS_READ_PERMISSION = USERS_PERMISSIONS.DIRECTORY_READ

export const systemUsersKeys = { all: ['system-users'] as const }

/** Referência única do vazio: `?? []` cru criaria array novo a cada render, e a
 *  lista é dep do `useMemo` do `value` do ChatBotContext. */
const NENHUM: ISystemUser[] = []

/**
 * RBAC de dados: decide se o `GET /users/directory` deve sair. Sem `user` carregado (ainda
 * `null`) ou sem a permission, não busca — a lista permanece `[]` e nenhum request
 * sai. Pura e exportada de propósito: a decisão é o que erra caro, e testá-la não
 * pode exigir montar provider.
 */
export function shouldFetchSystemUsers(user: AuthUser | null): boolean {
  if (!user) return false
  return checkPermission(user.permissions, SYSTEM_USERS_READ_PERMISSION)
}

export interface SystemUsersResult {
  systemUsers: ISystemUser[]
  isLoading: boolean
  isError: boolean
}

/**
 * Lista de usuários do sistema, app-wide e gated por `users:directory:read`.
 *
 * Vive fora do `ChatBotContext` porque o provider só cobre `/chatbot`: quem resolve
 * nome de autor em `/admin`, `/conta` ou `/notifications` (o [[../components/UserDisplay]])
 * precisa da lista sem arrastar um provider de canal pra árvore. `QueryProvider`
 * (root layout) e `AuthProvider` (`(private)/PrivateProviders.tsx`) cobrem toda rota
 * privada — este hook funciona em qualquer uma delas.
 *
 * Lista vazia é o caminho COMUM, não erro: sem a permission a query nem dispara.
 */
export function useSystemUsers(): SystemUsersResult {
  const { user } = useAuth()
  const podeVer = shouldFetchSystemUsers(user)

  const query = useQuery<ISystemUser[]>({
    queryKey: systemUsersKeys.all,
    queryFn: async () => {
      try {
        return await fetchSystemUsers()
      } catch (err) {
        // Defesa dupla: permission revogada entre o gate e a resposta do back.
        // 403 é ausência de grant, não falha — degrada pra vazio, sem estado de erro.
        // EXCETO o 403 de rede: o desbloqueio só refaz consulta em erro, e `[]` ficaria pra sempre.
        if (err instanceof ApiError && err.status === 403 && !ehRedeBloqueada(err)) return []
        throw err
      }
    },
    enabled: podeVer,
    staleTime: 5 * 60_000,
    // O effect que isto substituiu refazia o GET a cada refresh do /auth/me (5 min).
    // Sem intervalo a lista congelava pelo resto da sessão: `refetchOnWindowFocus` é
    // `false` global (`providers/QueryProvider.tsx:17`), ninguém invalida esta key, e
    // o observer do ChatBotProvider nunca desmonta dentro de /chatbot.
    refetchInterval: 5 * 60_000,
  })

  // Gate FECHADO devolve vazio na hora, sem esperar load duro: `enabled: false` só
  // impede o request — o `data` sobrevive no cache do TanStack, e os nomes (e o link
  // pro /admin/users) continuariam resolvendo depois de o grant ser revogado.
  return {
    systemUsers: podeVer ? (query.data ?? NENHUM) : NENHUM,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
